import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, Observable, take, tap } from 'rxjs';

import { ActionState, LocalStorageService } from '@ui/legacy-lib';
import { SearchProfileStore } from '@ui/tenant-pool/shared/search-profile/data-access';
import {
  CustomQuestionAnswer,
  HierarchicalRootQuestionResponse,
  SearchProfile
} from '@ui/shared/model';

import { map } from 'rxjs/operators';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import {
  HierarchicalQuestionResponse,
  SaveCustomQuestionsAnswers
} from '../../+state/custom-questions/custom-questions.actions';
import {
  getCustomQuestionActionState,
  getSaveAnswersActionState
} from '../../+state/custom-questions/custom-questions.selectors';

export const StoredRegistrationDataGuard: CanActivateFn = () => {
  const store = inject(Store);
  const searchProfileStore = inject(SearchProfileStore);
  const localStorageService = inject(LocalStorageService);

  const searchProfile = localStorageService.getItem<SearchProfile>(
    storageKeys.alreadyExistingUserSearchProfile
  );
  const hierarchicalQuestions = localStorageService.getItem<
    HierarchicalRootQuestionResponse[]
  >(storageKeys.alreadyExistingHierarchicalQuestions);
  const customQuestions = localStorageService.getItem<CustomQuestionAnswer[]>(
    storageKeys.alreadyExistingCustomQuestions
  );

  const hasStoredData =
    !!searchProfile || !!hierarchicalQuestions || !!customQuestions;

  if (!hasStoredData) {
    return true;
  }

  const observables: Observable<ActionState>[] = [];

  if (hierarchicalQuestions) {
    store.dispatch(new HierarchicalQuestionResponse(hierarchicalQuestions));
    observables.push(
      store.select(getCustomQuestionActionState).pipe(
        filter(state => !state.pending),
        take(1),
        tap(state => {
          if (!state.error) {
            localStorageService.removeItem(
              storageKeys.alreadyExistingHierarchicalQuestions
            );
          }
        })
      )
    );
  }

  if (customQuestions) {
    store.dispatch(new SaveCustomQuestionsAnswers(customQuestions));
    observables.push(
      store.select(getSaveAnswersActionState).pipe(
        filter(state => !state.pending),
        take(1),
        tap(state => {
          if (!state.error) {
            localStorageService.removeItem(
              storageKeys.alreadyExistingCustomQuestions
            );
          }
        })
      )
    );
  }

  if (searchProfile) {
    searchProfileStore.saveSearchProfile({ searchProfile, addToState: false });
    observables.push(
      toObservable(searchProfileStore.saveSearchProfileActionState).pipe(
        filter(state => !state.pending),
        take(1),
        tap(state => {
          if (!state.error) {
            localStorageService.removeItem(
              storageKeys.alreadyExistingUserSearchProfile
            );
          }
        })
      )
    );
  }

  return combineLatest(observables).pipe(
    take(1),
    map(() => true)
  );
};
